import { newId } from '@/lib/ids';
import { CONCRETE_MAX_M3, countsAsProduction, estimatedTonnes, type ActivityDraft } from '@/lib/diary';
import { tonnes as fmtTonnes } from '@/lib/format';
import { Button } from '@/components/ui/Button';
import { NumberInput, SelectInput, TextInput } from '@/components/ui/Field';
import type { ActivityType } from '@/types/models';

interface Option { value: string; label: string }

interface Props {
  /** The activities this machine may record, its own first (activityTypesFor). */
  activityTypes: ActivityType[];
  /** Every activity type, so a saved activity still shows if the list above
   *  no longer offers it (for instance after the machine was changed). */
  allActivityTypes?: ActivityType[];
  activities: ActivityDraft[];
  defaultPayload: number;
  locations: Option[];
  blasts: Option[];
  /** Concrete branches (0052). */
  branches: Option[];
  /** 0068: the weighbridge ticket's lists. A product brings its material. */
  materials?: Option[];
  products?: (Option & { material_id?: string | null })[];
  /** 0068: the diary machine hauls (ADT / dump truck). */
  machineHauling?: boolean;
  /** 0068: a branch diary — no quarry blasts there. */
  atBranch?: boolean;
  onChange: (next: ActivityDraft[]) => void;
}

const CONCRETE_LABEL = { delivered: 'Concrete delivered', pumped: 'Concrete pumped' } as const;

/**
 * What the machine actually did. Loads are counted with buttons big enough
 * to hit while holding a tablet, and the tonnes they represent appear as
 * they are entered — the operator sees the consequence of the number they
 * are typing.
 */
export function Activities({
  activityTypes, allActivityTypes, activities, defaultPayload, locations, blasts, branches,
  materials = [], products = [], machineHauling, atBranch, onChange,
}: Props) {
  const lookup = allActivityTypes ?? activityTypes;
  function add() {
    const first = activityTypes[0];
    onChange([
      ...activities,
      {
        id: newId(),
        activity_type_id: first?.id ?? '',
        hours: null,
        loads: first?.captures_loads ? 0 : null,
        payload_assumption_t: first?.captures_loads ? defaultPayload : null,
        tonnes_measured: null,
        volume_m3: null,
        branch_site_id: null,
        blast_id: null,
        source_location_id: null,
        destination_location_id: null,
        material_id: null,
        product_id: null,
        comments: null,
      },
    ]);
  }

  function update(id: string, patch: Partial<ActivityDraft>) {
    onChange(activities.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  }

  function remove(id: string) {
    onChange(activities.filter((a) => a.id !== id));
  }

  if (!activities.length) {
    return (
      <>
        <p className="small muted" style={{ marginTop: 0 }}>
          No activities recorded yet. Add what the machine was doing — loading,
          hauling, feeding the crusher, road maintenance, concrete delivered or pumped.
        </p>
        <Button variant="secondary" onClick={add}>+ Add activity</Button>
      </>
    );
  }

  return (
    <>
      {activities.map((a) => {
        const type = lookup.find((t) => t.id === a.activity_type_id);
        const est = estimatedTonnes(a);
        const concrete = type?.concrete_measure ?? null;
        const counts = !!type && countsAsProduction(type, machineHauling);
        const asTicket = !!type?.captures_loads && !concrete;
        const options = activityTypes.some((t) => t.id === a.activity_type_id) || !type
          ? activityTypes
          : [type, ...activityTypes];

        return (
          <div className="activity" key={a.id}>
            <div className="activity__top">
              <SelectInput
                id={`act-${a.id}`}
                label=""
                value={a.activity_type_id || null}
                options={options.map((t) => ({ value: t.id, label: t.name }))}
                placeholder="— choose an activity —"
                onChange={(v) => {
                  const t = lookup.find((x) => x.id === v);
                  update(a.id, {
                    activity_type_id: v ?? '',
                    // Branch and m³ belong to concrete activities only.
                    branch_site_id: t?.concrete_measure ? a.branch_site_id : null,
                    volume_m3: t?.concrete_measure ? a.volume_m3 : null,
                    loads: t?.captures_loads ? (a.loads ?? 0) : null,
                    payload_assumption_t: t?.captures_loads
                      ? (a.payload_assumption_t ?? defaultPayload)
                      : null,
                  });
                }}
              />
              <button
                className="btn btn--ghost"
                style={{ minHeight: 36 }}
                onClick={() => remove(a.id)}
                aria-label="Remove activity"
              >
                Remove
              </button>
            </div>

            {type?.captures_loads && !concrete && (
              <div className="row" style={{ gap: 'var(--sp-4)', flexWrap: 'wrap', marginBottom: 'var(--sp-3)' }}>
                <div>
                  <div className="field__label" style={{ marginBottom: 4 }}>Loads</div>
                  <div className="loads">
                    <button
                      className="loads__btn"
                      onClick={() => update(a.id, { loads: Math.max(0, (a.loads ?? 0) - 1) })}
                      aria-label="One load fewer"
                    >
                      −
                    </button>
                    <input
                      className="loads__value"
                      type="number"
                      inputMode="numeric"
                      min={0}
                      value={a.loads ?? 0}
                      onChange={(e) => update(a.id, { loads: Math.max(0, Number(e.target.value) || 0) })}
                    />
                    <button
                      className="loads__btn"
                      onClick={() => update(a.id, { loads: (a.loads ?? 0) + 1 })}
                      aria-label="One load more"
                    >
                      +
                    </button>
                  </div>
                </div>

                <div className="activity__tonnes">
                  <span className="activity__tonnes-value">{fmtTonnes(est)}</span>
                  <span className="small muted">
                    t estimated · {a.payload_assumption_t ?? defaultPayload} t per load
                  </span>
                </div>
              </div>
            )}

            {concrete ? (
              <div className="form-grid">
                <SelectInput
                  id={`branch-${a.id}`}
                  label="Branch"
                  required
                  value={a.branch_site_id}
                  options={branches}
                  placeholder="— choose the branch —"
                  onChange={(v) => update(a.id, { branch_site_id: v })}
                />

                <NumberInput
                  id={`m3-${a.id}`}
                  label={concrete === 'pumped' ? 'Amount pumped' : 'Amount of concrete delivered'}
                  suffix="m³"
                  required
                  min={0}
                  max={CONCRETE_MAX_M3}
                  step={0.5}
                  value={a.volume_m3}
                  onChange={(v) => update(a.id, { volume_m3: v })}
                  help={`${CONCRETE_LABEL[concrete]} — counted on the Concrete dashboard for this branch.`}
                />

                <NumberInput
                  id={`hrs-${a.id}`}
                  label="Hours on this activity"
                  suffix="h"
                  min={0}
                  max={24}
                  step={0.25}
                  value={a.hours}
                  onChange={(v) => update(a.id, { hours: v })}
                />

                <div className="field--full">
                  <TextInput
                    id={`cmt-${a.id}`}
                    label="Comment"
                    value={a.comments ?? ''}
                    onChange={(v) => update(a.id, { comments: v || null })}
                  />
                </div>
              </div>
            ) : (
            <div className="form-grid">
              {type?.production_on_hauling_only && !machineHauling ? (
                <p className="small muted field--full" style={{ margin: 0 }}>
                  On this machine {type.name} records the work only. The tonnes are counted
                  from the ADTs' own entries, so the same loads are not counted twice.
                </p>
              ) : null}

              {!atBranch && (type?.consumes_blast_stock || type?.requires_blast) ? (
                <SelectInput
                  id={`blast-${a.id}`}
                  label="Blast"
                  required={type?.requires_blast && counts}
                  value={a.blast_id}
                  options={blasts}
                  onChange={(v) => update(a.id, { blast_id: v })}
                  help={counts ? 'Material hauled from this blast is deducted from its stock.' : undefined}
                />
              ) : null}

              <SelectInput
                id={`src-${a.id}`}
                label="Source"
                value={a.source_location_id}
                options={locations}
                onChange={(v) => update(a.id, { source_location_id: v })}
              />

              <SelectInput
                id={`dst-${a.id}`}
                label="Destination"
                required={type?.requires_destination && counts}
                value={a.destination_location_id}
                options={locations}
                onChange={(v) => update(a.id, { destination_location_id: v })}
              />

              {asTicket && (
                <SelectInput
                  id={`prod-${a.id}`}
                  label="Product"
                  value={a.product_id ?? null}
                  options={products}
                  onChange={(v) => {
                    const p = products.find((x) => x.value === v);
                    update(a.id, {
                      product_id: v,
                      // As on the weighbridge ticket, the product brings its material.
                      material_id: p?.material_id ?? a.material_id ?? null,
                    });
                  }}
                />
              )}

              {asTicket && (
                <SelectInput
                  id={`mat-${a.id}`}
                  label="Material"
                  value={a.material_id ?? null}
                  options={materials}
                  onChange={(v) => update(a.id, { material_id: v })}
                />
              )}

              {!type?.captures_loads && (
                <NumberInput
                  id={`tm-${a.id}`}
                  label={type?.default_unit === 'tonnes' ? 'Tonnes' : 'Quantity'}
                  suffix={type?.default_unit === 'm3' ? 'm³' : 't'}
                  min={0}
                  step={0.1}
                  value={a.tonnes_measured}
                  onChange={(v) => update(a.id, { tonnes_measured: v })}
                />
              )}

              <NumberInput
                id={`hrs-${a.id}`}
                label="Hours on this activity"
                suffix="h"
                min={0}
                max={24}
                step={0.25}
                value={a.hours}
                onChange={(v) => update(a.id, { hours: v })}
              />

              <div className="field--full">
                <TextInput
                  id={`cmt-${a.id}`}
                  label="Comment"
                  value={a.comments ?? ''}
                  onChange={(v) => update(a.id, { comments: v || null })}
                />
              </div>
            </div>
            )}
          </div>
        );
      })}

      <Button variant="secondary" onClick={add}>+ Add activity</Button>
    </>
  );
}
